import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { BoldInfoCircle, CheckIcon } from "../../Icon";
import { TextField, InputAdornment, useTheme, Box } from "@mui/material";
import { alpha, borderColor, color, styled } from "@mui/system";
import Icon from "../../Icon/Icon";

/***
 * @param {boolean} disabled
 * @param {string} type -	Type of the input element. It should be a valid HTML5 input type.
 * @param {string} label - Descriptive input title
 * @param {string} value - Input content
 * @param {event} onChange - Event onChange
 * @param {boolean} required - If true, the label is displayed as required and the input element is required.
 * @param {boolean} isValid - If true, the label is displayed in an error state.
 * @param {string} helperText - Helps the user to know when a validation has failed
 * @param {string} variant -  'filled' | 'outlined' (default) | 'standard'
 * @param {boolean} fullWidth - Occupies all available width
 * @param {boolean} showSuccessValidation - If true, shows success styles when the input is valid.
 * @param onFocus - will return a memoized version of the callback that only changes if one of the inputs has changed.
 * @param onBlur - will return a memoized version of the callback that only changes if one of the inputs has changed.
 * @param {boolean} multiline - If true, a textarea element is rendered instead of an input.
 * @param {number} rows - Number of rows to display when multiline option is set to true.
 * @param {object} {...rest} All other properties you wish to add based on the MaterialUI library
 */

/**
 * Componente TextField personalizado con estilos condicionales.
 */
const CustomTextField = styled(({ isValid, showSuccessValidation, ...props }) => (
  <TextField {...props} />
))(({ theme, isValid, required, value, variant, showSuccessValidation }) => {
  // Función auxiliar para obtener el color del borde según el estado
  const getBorderColor = (isValid, isFocused) => {
    const defaultColor = theme.palette.blueGray200.main;
    if (isValid === false) return theme.palette.error.main; // Siempre error si es inválido
    if (isValid === true && showSuccessValidation) return theme.palette.success.main; // Verde si es válido y showSuccessValidation es true
    if (isValid === null) return defaultColor; // Verde si es válido y showSuccessValidation es true
    if (required && !value) return theme.palette.error.main; // Error si es inválido o requerido y vacío
    if (isFocused) return theme.palette.primary.main; // Color primario cuando está enfocado
    return defaultColor; // Color secundario por defecto
  };

  // Función auxiliar para obtener el color del label según el estado
  const getLabelColor = (isValid, isFocused) => {
    const defaultColor = theme.palette.secondary.main;
    if (isValid === false) return theme.palette.error.main; // Error si es inválido o requerido y vacío
    if (isValid === true && showSuccessValidation) return theme.palette.success.main; // Verde si es válido y showSuccessValidation es true
    if (isValid === null) return defaultColor; // Verde si es válido y showSuccessValidation es true
    if (required && !value) return theme.palette.error.main; // Error si es inválido o requerido y vacío
    if (isFocused) return theme.palette.primary.main; // Color primario cuando está enfocado
    return defaultColor; // Color secundario por defecto
  };

  return {
    // Estilos base para todos los inputs
    "& .MuiFilledInput-root, & .MuiInput-root": {
      ...(variant !== "outlined" && {
        borderBottom: `2px solid ${getBorderColor(isValid, false)}`, // Color inicial sin foco
        "&:hover": {
          borderBottom: `2px solid ${theme.palette.secondary.dark}`,
        },
        "&.Mui-focused": {
          borderBottom: `2px solid ${getBorderColor(isValid, true)}`, // Color con foco
        },
      }),
    },

    // Estilos específicos para el input
    "& .MuiInput-input": {
      lineHeight: "1.6",
    },

    // Estilos para el variant 'filled'
    "& .MuiFilledInput-root": {
      borderRadius: theme.shape.borderRadius,
    },

    // Estilos para el variant 'outlined'
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: getBorderColor(isValid, false), // Color inicial sin foco
      },
      "&:hover fieldset": {
        borderColor: theme.palette.secondary.dark,
      },
      "&.Mui-focused fieldset": {
        borderColor: getBorderColor(isValid, true), // Color con foco
        borderWidth: "2px",
      },
    },

    // Estilos para la etiqueta (label)
    "& .MuiFormLabel-root": {
      color: getLabelColor(isValid, false), // Color inicial sin foco
      letterSpacing: 0,
      fontWeight: 600,
      "&.Mui-focused": {
        color: getLabelColor(isValid, true), // Color con foco
      },
      "&.MuiInputLabel-shrink": {
        top: variant !== "filled" && 0,
        transform: variant === "filled" && "translate(12px, -6px) scale(0.85)",
        margin: variant === "filled" && "7px -5px",
        backgroundColor:
          variant !== "filled" && alpha(theme.palette.common.white, 0.9),
        borderRadius: "25%",
      },
    },

    '& .MuiInputBase-root': {
      padding: '3px',
      paddingLeft: '14px',
      color: theme.palette.oxfordBlue.main
    },
    '& .MuiInputBase-input': {
      padding: 0,
    },
    '& .MuiInputBase-inputMultiline': {
      marginTop: '2px',
    },

    // Estilos para el placeholder
    "& .MuiInputBase-input::placeholder": {
      color: theme.palette.secondary.main,
      ...(isValid === false && {
        color: theme.palette.error.main,
      }),
    },

    // Estilos para el texto de ayuda (helperText)
    "& .MuiFormHelperText-root": {
      color: theme.palette.error.main,
      fontSize: "0.775rem",
    },
  };
});

/**
 * Componente Adornment para mostrar íconos de validación.
 */
export const ValidationAdornment = ({ isValid, isFocused, showSuccessValidation }) => {
  const theme = useTheme();

  if (!isFocused) return null;

  if ((isValid === true && showSuccessValidation) || (isValid === true && isFocused)) {
    return (
      <InputAdornment 
        position="end"
        sx={{
          position: 'absolute', // Posicionamiento absoluto
          bottom: '8px', // Distancia desde la parte inferior
          right: '8px', // Distancia desde la parte derecha
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.success.main,
            borderRadius: "100%",
            width: 18,
            height: 18,
            display: 'grid',
            placeContent: 'center'
          }}
        >
          <CheckIcon color={theme.palette.common.white} size={20} />
        </Box>
      </InputAdornment>
    );
  }

  if (isValid === false) {
    return (
      <InputAdornment 
        position="end"
        sx={{
          position: 'absolute', // Posicionamiento absoluto
          bottom: '8px', // Distancia desde la parte inferior
          right: '8px', // Distancia desde la parte derecha
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.common.white,
            borderRadius: "100%",
            width: 18,
            height: 18,
            display: 'grid',
            placeContent: 'center'
          }}
        >
          <BoldInfoCircle color={theme.palette.error.main} size={22} />
        </Box>
      </InputAdornment>
    );
  }

  return null;
};

const StartIcon = ({ iconName }) => {
  
  const theme = useTheme();

  if (!iconName) return null;

  return (
    <InputAdornment 
      position="start"
      sx={{
        margin: '0', // Posicionamiento absoluto
        marginRight: '4px', // Posicionamiento absoluto
      }}
    >
      <Icon size={20} color={theme.palette.blueGray500.main} name={iconName} />
    </InputAdornment>
  )
}

export const EGInput = ({
  disabled = false,
  label = "",
  multiline = false,
  onChange = () => {},
  required = false,
  rows = 1,
  type = "text",
  helperText: initialHelperText = "",
  isValid,
  value = "",
  variant = "outlined",
  showSuccessValidation = false,
  showHelpText = true,
  iconName = "",
  autoComplete = "off",
  ...rest
}) => {

  const [t] = useTranslation("components");

  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(() => setIsFocused(false), []);

  const helperText = initialHelperText || t("input.this_field_is_required");

  return (
    <CustomTextField
      disabled={disabled}
      type={type}
      label={label}
      value={value}
      onChange={onChange}
      required={required}
      error={isValid === false}
      variant={variant}
      fullWidth
      isValid={isValid}
      showSuccessValidation={showSuccessValidation}
      onFocus={handleFocus}
      onBlur={handleBlur}
      multiline={multiline}
      rows={rows}
      autoComplete={autoComplete}
      InputProps={{
        ...(iconName && {
          startAdornment: (
            <StartIcon iconName={iconName} />
          ),
        }),
        endAdornment: (
          <ValidationAdornment 
            isValid={isValid} 
            isFocused={isFocused} 
            showSuccessValidation={showSuccessValidation}
          />
        ),
      }}
      {...showHelpText && ({
        helperText: (isValid === false && `* ${helperText}`) 
        ||  (required && !value && `* ${helperText}`)
      })}
      {...rest}
    />
  );
};